// @flow
import * as React from 'react';
import { useMutation, useReactiveVar, useApolloClient } from '@apollo/client';
import { evolve, map, path } from 'ramda';

import NewInfo from '../../../../basic-components/new-info';
import Button from '../../../../basic-components/button';
import ANALYTICS from '../../../../analytics/stock';
import { ActivateShowcase } from '../../../../shared-queries/ActivateShowcase.graphql';
import { stockFiltersVar } from '../../../../utils/apollo-cache';
import { omitTypenames } from '../../../../utils/apollo-helpers';
import { Products } from '../../../../shared-queries/Products.graphql';
import ShowcaseFragment from '../../../../shared-queries/ShowcaseFragment.graphql';

import AuShine from './au-shine.svg';
import cs from './style.pcss';
import t from './locale';

type Props = {
  showcaseId: string,
  limit: number,
  onClose: (value: string) => void,
};

export default function AuFreePopup({
  showcaseId,
  limit,
  onClose,
}: Props): React.Node {
  const client = useApolloClient();

  const filters = omitTypenames(useReactiveVar(stockFiltersVar));

  React.useEffect(() => {
    ANALYTICS.showFreeAuPopup();
  }, []);

  const showcaseFragment = client.readFragment({
    fragment: ShowcaseFragment,
    id: `Showcase:${showcaseId}`,
  });

  const [activate, { loading }] = useMutation(ActivateShowcase, {
    update(cache, { data }) {
      if (!data.showcasesActivate.errors.length) {
        const productsData = cache.readQuery({
          query: Products,
          variables: {
            filters,
          },
        });

        const updatedProducts = evolve(
          {
            products: {
              nodes: map((node) => {
                const selected = path(['showcasesActivate', 'selected'], data);
                if (!selected) {
                  return node;
                }

                const selectedData = selected.find(
                  (s) => s.sourceId === node.id
                );

                if (!selectedData) {
                  return node;
                }

                return evolve(
                  {
                    listings: map((l) =>
                      selectedData.listingIds.includes(l.id)
                        ? { ...l, selected: true }
                        : l
                    ),
                  },
                  node
                );
              }),
            },
          },
          productsData
        );

        cache.writeQuery({
          query: Products,
          data: updatedProducts,
          variables: {
            filters,
          },
        });
      }
    },
  });

  const handleClick = () => {
    ANALYTICS.listFreeOnAu(limit);

    return activate({
      variables: {
        input: {
          ids: [showcaseId],
          publishMostRated: limit,
        },
      },
    }).finally(() => {
      onClose('viewed');
      client.writeFragment({
        fragment: ShowcaseFragment,
        data: { ...showcaseFragment, active: true, anySelected: true },
        id: `Showcase:${showcaseId}`,
      });
    });
  };

  return (
    <NewInfo
      contentClassName={cs.content}
      className={cs.tooltip}
      onClose={() => {
        onClose('viewed');
      }}
      arrowRightMargin="155px"
    >
      <AuShine className={cs.icon} />
      <div>
        <div className={cs.title}>{t('title')}</div>
        <div className={cs.text}>{t('text')}</div>
        <Button
          size="small"
          className={cs.action}
          onClick={handleClick}
          loading={loading}
        >
          {t('action', { count: limit })}
        </Button>
      </div>
    </NewInfo>
  );
}
